import React, { useRef, useCallback } from 'react'

import { Text, Label, Title } from 'components/typo'
import Button from 'components/shared/Button'
import Ratio from 'components/shared/Ratio'
import Image from 'components/shared/Image'
import Layout from 'components/shared/Layout'

import useLabelTitleTextAnimation from 'hooks/useLabelTitleTextAnimation'
import useResizeIndex from 'hooks/useResizeIndex'
import useAnimateInView from 'hooks/useAnimateInView'
import useComponentSize from '@rehooks/component-size'

import classnames from 'classnames/bind'
import css from './styles.module.scss'

const cx = classnames.bind(css)

const SimpleTextNextToImage = ({
  darkMode,
  headline,
  image,
  imagePosition,
  label,
  text,
  button,
}) => {
  const componentRef = useRef()
  const elementRef = useRef()
  const imageRef = useRef()

  const { width, height } = useComponentSize(componentRef)
  const resizeIndex = useResizeIndex()

  const tl = useLabelTitleTextAnimation(
    {
      elementRef,
      components: [
        {
          ref: imageRef,
          type: 'zoom',
        },
      ],
      linesClass: css.line,
    },
    [width, height, resizeIndex]
  )

  const [inViewRef] = useAnimateInView(
    {
      triggerOnce: true,
      ref: componentRef,
      animateIn: () => tl.restart(true),
    },
    [width, height, resizeIndex]
  )

  const setRef = useCallback((node) => {
    elementRef.current = node
    inViewRef(node)
  }, [])

  return (
    <div ref={componentRef}>
      <div ref={setRef}>
        <Layout
          className={cx(css.SimpleTextNextToImage, imagePosition)}
          type="wrapperv4"
        >
          <div className={css.textWrapper}>
            {label && (
              <div className={css.label}>
                <Label
                  className={css.labelNode}
                  theme={darkMode ? 'purple-anemone' : 'blue-500'}
                >
                  {label}
                </Label>
              </div>
            )}
            {headline && (
              <div className={css.title}>
                <Title
                  className={css.titleNode}
                  theme={darkMode ? 'white' : 'dark'}
                >
                  {headline}
                </Title>
              </div>
            )}
            {text && (
              <div className={css.text}>
                <Text
                  className={css.textNode}
                  theme={darkMode ? 'grey-400' : ''}
                >
                  {text}
                </Text>
              </div>
            )}
            {button && (
              <div className={css.button}>
                <Button {...button} />
              </div>
            )}
          </div>
          <div className={cx(css.imageWrapper, { darkMode })} ref={imageRef}>
              {image && <Image {...image} fit="contain" />}
          </div>
        </Layout>
      </div>
    </div>
  )
}

export default SimpleTextNextToImage
